import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { fmt, fmtDate } from '@dcc-erp/shared';
import type { PettyCashCount } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { diffColor } from './pc-helpers';
import { HttpError } from '../../lib/api';
import {
  useApprovePettyCashCountStep,
  useRejectPettyCashCountStep,
  useReturnPettyCashCount,
  useSubmitPettyCashCount,
} from '../../lib/queries/petty-cash';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

// ============================================================
// Cash-count approval panel (PCC) — clone of PCEntryApprovalPanel. Maker
// (pettycash.count.create) submits a DRAFT/RETURNED count; checker
// (pettycash.count.approve) approves/returns/rejects via DecisionModal. The
// over/short JV posts on final-step approval. 1-step chain (FINANCE_MANAGER).
// ============================================================

export interface PCCountApprovalPanelProps {
  companyId: string | undefined;
  count: PettyCashCount;
  onClose: () => void;
}

export function PCCountApprovalPanel({ companyId, count, onClose }: PCCountApprovalPanelProps) {
  const submitMut = useSubmitPettyCashCount(companyId);
  const approveMut = useApprovePettyCashCountStep(companyId);
  const rejectMut = useRejectPettyCashCountStep(companyId);
  const returnMut = useReturnPettyCashCount(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  const approvalStatus =
    count.approvalStatus ?? (count.status === 'PENDING_APPROVAL' ? 'PENDING' : null);
  const currentStep = count.currentStep ?? 0;
  const approvals = useMemo(
    () => (count.approvals ?? []).map((a) => ({ ...a, id: a.stepNo })),
    [count.approvals],
  );
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';
  const diff = Number(count.difference);

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(count.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          countId: count.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          countId: count.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          countId: count.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['pc-counts', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pc-count-approval-panel"
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              สายอนุมัติการนับเงินสด — {count.voucherNo}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              นับได้ {fmt(Number(count.countedBalance))} · บัญชี{' '}
              {fmt(Number(count.expectedBalance))} · ผลต่าง{' '}
              <b style={{ color: diffColor(diff) }}>
                {diff > 0 ? '+' : ''}
                {fmt(diff)}
              </b>{' '}
              บาท
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflow: 'auto',
          }}
        >
          {approvalStatus === 'REJECTED' && count.rejectedReason && (
            <div
              style={{
                background: 'var(--negative-bg, #fdecea)',
                border: '1px solid var(--negative)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 13,
              }}
              data-testid="pc-count-rejected-reason"
            >
              <strong style={{ color: 'var(--negative)' }}>เหตุผลที่ไม่อนุมัติ:</strong>{' '}
              {count.rejectedReason}
            </div>
          )}
          {approvalStatus === 'RETURNED' && count.returnedReason && (
            <div
              style={{
                background: 'var(--warning-bg, #fef3e8)',
                border: '1px solid var(--warning)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 13,
              }}
              data-testid="pc-count-returned-reason"
            >
              <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
              {count.returnedReason}
            </div>
          )}
          {isPending && (
            <div className="meta">
              <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
            </div>
          )}

          <ApprovalTimeline
            approvals={approvals}
            isActive={isPending}
            currentStep={currentStep}
            emptyText="ยังไม่ส่งอนุมัติ"
            leadLine={
              count.submittedBy ? (
                <div className="meta">
                  ส่งอนุมัติโดย {count.submittedBy}
                  {count.submittedAt ? ` · ${fmtDate(count.submittedAt)}` : ''}
                </div>
              ) : undefined
            }
            trailLine={
              count.status === 'POSTED' && count.approvedBy ? (
                <div className="meta">
                  อนุมัติโดย {count.approvedBy}
                  {count.approvedAt ? ` · ${fmtDate(count.approvedAt)}` : ''}
                </div>
              ) : undefined
            }
          />

          {actionError && (
            <div
              style={{ color: 'var(--negative)', fontSize: 14 }}
              data-testid="pc-count-action-error"
            >
              {actionError}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="pettycash.count.create">
              <button
                className="btn primary"
                disabled={submitBusy}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="pettycash.count.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </div>
      </div>

      {showDecide && (
        <DecisionModal
          voucherNo={count.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาการนับเงินสด"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </div>
  );
}
